import React, { useState, useEffect, useMemo } from "react";
import "./Navbar.css";
import logo from "../../assets/images/logo.png";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../contextPage/CartContext";
import { API } from "../../services/api";

const getProductId = (item) => String(item.product_id ?? item.productId ?? item.id);
const parseImages = (images) => {
    try {
        if (Array.isArray(images)) return images;
        if (typeof images === "string") return JSON.parse(images || "[]");
        return [];
    } catch {
        return [];
    }
};
const getCartItemName = (item) =>
    item.productName ?? item.product?.productName ?? item.name ?? item.product?.name ?? "Product";

const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const [wishlistCount, setWishlistCount] = useState(0);
    const { cartCount = 0, cart = [], cartSubtotal = 0, removeFromCart, getCartItemUnitPrice } = useCart();

    const navigate = useNavigate();

    const handleCartClick = () => {
        if (window.innerWidth > 768) {
            setIsCartOpen(true);
        } else {
            navigate("/cart");
        }
    };
    const closeCartDrawer = () => setIsCartOpen(false);

    // Top-level categories only
    const topCategories = useMemo(
        () => ["Rings", "Earrings", "Bracelets", "Kada","Kids", "All Jewellery"],
        []
    );

    const toggleMobileMenu = (e) => {
        if (e) e.stopPropagation();
        setIsMobileMenuOpen((s) => !s);
    };
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    // const handleTopNavClick = (menu) => {
    //     if (menu === "All Jewellery") {
    //         navigate("/products");
    //     } else {
    //         navigate(`/products?category=${encodeURIComponent(menu)}`);
    //     }
    //     setIsMobileMenuOpen(false);
    // };
    const handleTopNavClick = (menu) => {
        if (menu === "All Jewellery") {
            navigate("/products");
        } else if (menu === "Kids") {
            // ✅ this triggers Shop For filter (type)
            navigate(`/products?type=${encodeURIComponent("Kids")}`);
        } else {
            // normal category filters
            navigate(`/products?category=${encodeURIComponent(menu)}`);
        }

        setIsMobileMenuOpen(false);
    };
    // 🔍 Search submit → redirect to results page
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
        setShowSearch(false);
    };

    // Wishlist badge from localStorage (cart count comes from CartContext)
    useEffect(() => {
        try {
            const wl = JSON.parse(localStorage.getItem("wishlist") || "[]");
            setWishlistCount(Array.isArray(wl) ? wl.length : 0);
        } catch {
            setWishlistCount(0);
        }
    }, []);

    // Mobile menu: lock body scroll and close on Escape
    useEffect(() => {
        if (!isMobileMenuOpen) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && closeMobileMenu();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [isMobileMenuOpen]);

    // Cart drawer: lock body scroll and close on Escape (desktop)
    useEffect(() => {
        if (!isCartOpen) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && closeCartDrawer();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [isCartOpen]);

    return (
        <>
            <div className="navbar-fixed-wrap">
                {/* promo strip */}
                <div className="nav-promo-bar">
                    <span>Flat 20% Off on Silver Jewellery</span>
                </div>

                {/* main nav */}
                <header className="nav-shell">
                <div className="nav-inner nav-container container">
                    {/* left */}
                    <div
                        className="nav-left"
                        onClick={() => navigate("/")}
                        role="button"
                        aria-label="Go home"
                    >
                        <button
                            className="nav-hamburger"
                            onClick={(e) => { e.stopPropagation(); toggleMobileMenu(e); }}
                            aria-label="Menu"
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                        <img src={logo} alt="Logo" className="nav-logo-image" />
                    </div>

                    {/* center */}
                    <nav className="nav-center">
                        <div className="nav-menu">
                            {topCategories.map((menu, idx) => (
                                <button
                                    key={idx}
                                    className="nav-menu-item nav-link-btn nav-top-link"
                                    onClick={() => handleTopNavClick(menu)}
                                >
                                    {menu}
                                </button>
                            ))}
                        </div>
                    </nav>

                    {/* right */}
                    <div className="nav-right">
                        <button
                            className="nav-icon-btn"
                            onClick={() => navigate("/profile")}
                            aria-label="Profile"
                        >
                            <i className="fas fa-user"></i>
                        </button>

                        <div className="nav-divider" />

                        {/* search */}
                        <div className="nav-search-inline">
                            <button
                                className="nav-icon-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowSearch((s) => !s);
                                }}
                                aria-label="Search"
                            >
                                <i className="fas fa-search"></i>
                            </button>

                            {showSearch && (
                                <form
                                    className="nav-search-box"
                                    onSubmit={handleSearchSubmit}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search by name, SKU, tag..."
                                        className="nav-search-input"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                    />
                                </form>
                            )}
                        </div>

                        {/* cart */}
                        <button
                            className="nav-icon-btn nav-badge-wrap"
                            onClick={handleCartClick}
                            aria-label="Cart"
                        >
                            <i className="fas fa-shopping-bag"></i>
                            <span className="nav-badge">{cartCount}</span>
                        </button>
                    </div>
                </div>

                {/* mobile search */}
                <div className="nav-search-box mobileonly">
                    <form
                        className="nav-search-input-wrapper"
                        onSubmit={handleSearchSubmit}
                    >
                        <input
                            type="text"
                            placeholder="Search by name, SKU, tag..."
                            className="nav-search-input1"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button type="submit" className="nav-search-icon1">
                            <i className="fas fa-search"></i>
                        </button>
                    </form>
                </div>
            </header>
            </div>

            <div className="navbar-spacer" aria-hidden="true" />

            {/* mobile menu: overlay + left drawer */}
            <div
                className={`nav-mobile-overlay ${isMobileMenuOpen ? "nav-overlay-open" : ""}`}
                onClick={closeMobileMenu}
                onKeyDown={(e) => e.key === "Escape" && closeMobileMenu()}
                role="button"
                tabIndex={-1}
                aria-label="Close menu"
            />
            <nav
                className={`nav-mobile-menu nav-mobile-drawer ${isMobileMenuOpen ? "nav-open" : ""}`}
                aria-hidden={!isMobileMenuOpen}
            >
                <div className="nav-mobile-drawer-header">
                    <span className="nav-mobile-drawer-title">Menu</span>
                    <button
                        type="button"
                        className="nav-mobile-close"
                        onClick={closeMobileMenu}
                        aria-label="Close menu"
                    >
                        <span></span>
                        <span></span>
                    </button>
                </div>
                <div className="nav-mobile-inner">
                    <ul className="nav-mob-list">
                        {topCategories.map((menu, idx) => (
                            <li key={idx} className="nav-mob-item">
                                <button
                                    className="nav-link-btn"
                                    onClick={() => handleTopNavClick(menu)}
                                >
                                    {menu}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </nav>

            {/* Cart sidebar (desktop): slides in from right */}
            <div
                className={`nav-cart-overlay ${isCartOpen ? "nav-cart-overlay-open" : ""}`}
                onClick={closeCartDrawer}
                onKeyDown={(e) => e.key === "Escape" && closeCartDrawer()}
                role="button"
                tabIndex={-1}
                aria-label="Close cart"
            />
            <aside
                className={`nav-cart-drawer ${isCartOpen ? "nav-cart-drawer-open" : ""}`}
                aria-label="Cart"
            >
                <div className="nav-cart-drawer-header">
                    <h3 className="nav-cart-drawer-title">Your Cart</h3>
                    <button
                        type="button"
                        className="nav-cart-drawer-close"
                        onClick={closeCartDrawer}
                        aria-label="Close cart"
                    >
                        <span></span>
                        <span></span>
                    </button>
                </div>
                <div className="nav-cart-drawer-body">
                    {!cart || cart.length === 0 ? (
                        <p className="nav-cart-empty">Your cart is empty</p>
                    ) : (
                        <>
                            <ul className="nav-cart-list">
                                {cart.map((item) => {
                                    const pid = getProductId(item);
                                    const imgs = parseImages(item.images)?.length ? parseImages(item.images) : parseImages(item.product?.images) ?? [];
                                    const imgUrl = imgs.length > 0 ? `${API.imagePath}${imgs[0]}` : "/placeholder.jpg";
                                    const unitPrice = typeof getCartItemUnitPrice === "function" ? getCartItemUnitPrice(item) : Number(item.unitPrice ?? item.price ?? item.cost ?? 0);
                                    const qty = Number(item.quantity ?? 1);
                                    const name = getCartItemName(item);
                                    return (
                                        <li key={item.cart_item_id ?? pid} className="nav-cart-item">
                                            <img src={imgUrl} alt={name} className="nav-cart-item-img" onError={(e) => (e.target.src = "/placeholder.jpg")} />
                                            <div className="nav-cart-item-info">
                                                <span className="nav-cart-item-name">{name}</span>
                                                <span className="nav-cart-item-price">₹{unitPrice.toLocaleString()}</span>
                                                <button
                                                    type="button"
                                                    className="nav-cart-item-remove"
                                                    onClick={(e) => { e.stopPropagation(); removeFromCart(pid, item.variant_id); }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="nav-cart-drawer-footer">
                                <div className="nav-cart-subtotal">
                                    <span>Subtotal</span>
                                    <strong>₹{Number(cartSubtotal).toLocaleString()}</strong>
                                </div>
                                <button
                                    type="button"
                                    className="nav-cart-btn-view"
                                    onClick={() => { closeCartDrawer(); navigate("/cart"); }}
                                >
                                    View full cart
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
};

export default Navbar;
